function normalizeBasePath(value: string): string {
  if (!value || value === '/') return '';
  return `/${value.replace(/^\/+|\/+$/g, '')}`;
}

function normalizeSocketPath(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '/socket.io';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

function normalizeOptionalValue(value: string | undefined, fallback = ''): string {
  const trimmed = (value || '').trim();
  return trimmed || fallback;
}

function toGoogleReversedClientId(clientId: string): string {
  const suffix = '.apps.googleusercontent.com';
  if (!clientId.endsWith(suffix)) return '';
  const prefix = clientId.slice(0, -suffix.length);
  return `com.googleusercontent.apps.${prefix}`;
}

const defaultServerUrl = import.meta.env.DEV
  ? 'http://localhost:3000'
  : window.location.origin;

const serverBasePath = normalizeBasePath(import.meta.env.VITE_SERVER_BASE_PATH || '');
const defaultAppleNativeClientId = 'de.devnova.pickitup.ios';
const defaultAppleNativeRedirectUri = 'https://devnova.de/pickitupserver/api/auth/oauth/apple/callback';
const defaultGoogleServerClientId = '233965903548-vop27rv48km5f9b0v5r8cs36mkk1cqet.apps.googleusercontent.com';
const defaultGoogleIosClientId = '233965903548-sasruhsl2bl5n64p191bq88fjs7fv151.apps.googleusercontent.com';
const defaultGoogleAndroidClientId = '233965903548-cqv1ncjku0usquu3tg72or4c8raielt2.apps.googleusercontent.com';
const defaultIapPremiumProductId = 'de.devnova.pickitup.premium.lifetime_v3';
const defaultAndroidPackageName = 'de.devnova.pickitup.android';
const defaultNativeOAuthRedirectScheme = 'de.devnova.pickitup.android';
const defaultClientAppUrl = 'https://devnova.de/pickitup/';
const defaultNativeBillingRedirectScheme = 'de.devnova.pickitup';

/** Origin des PickItUp-Servers (ohne Pfad) */
export const SERVER_URL = (import.meta.env.VITE_SERVER_URL || defaultServerUrl).replace(/\/+$/, '');
export const SERVER_BASE_PATH = serverBasePath;
export const API_BASE_URL = `${SERVER_URL}${SERVER_BASE_PATH}/api`;
export const CLIENT_APP_URL = normalizeOptionalValue(
  import.meta.env.VITE_CLIENT_APP_URL,
  defaultClientAppUrl
);

/** Socket.IO HTTP-Pfad (wichtig für Reverse-Proxy unter /pickitupserver) */
export const SOCKET_PATH = normalizeSocketPath(
  import.meta.env.VITE_SOCKET_PATH || `${serverBasePath}/socket.io`
);

/** Native iOS Apple Sign-In (Capacitor Plugin) */
export const APPLE_NATIVE_CLIENT_ID = normalizeOptionalValue(
  import.meta.env.VITE_APPLE_NATIVE_CLIENT_ID,
  defaultAppleNativeClientId
);
export const APPLE_NATIVE_REDIRECT_URI = normalizeOptionalValue(
  import.meta.env.VITE_APPLE_NATIVE_REDIRECT_URI,
  defaultAppleNativeRedirectUri
);

/** Native Google Sign-In (Capacitor Plugin) */
export const GOOGLE_NATIVE_CLIENT_ID = normalizeOptionalValue(
  import.meta.env.VITE_GOOGLE_NATIVE_CLIENT_ID,
  ''
);
export const GOOGLE_NATIVE_SERVER_CLIENT_ID = normalizeOptionalValue(
  import.meta.env.VITE_GOOGLE_NATIVE_SERVER_CLIENT_ID,
  defaultGoogleServerClientId
);
export const GOOGLE_NATIVE_IOS_CLIENT_ID = normalizeOptionalValue(
  import.meta.env.VITE_GOOGLE_NATIVE_IOS_CLIENT_ID,
  defaultGoogleIosClientId
);
export const GOOGLE_NATIVE_ANDROID_CLIENT_ID = normalizeOptionalValue(
  import.meta.env.VITE_GOOGLE_NATIVE_ANDROID_CLIENT_ID,
  defaultGoogleAndroidClientId
);
export const GOOGLE_NATIVE_IOS_URL_SCHEME = normalizeOptionalValue(
  import.meta.env.VITE_GOOGLE_NATIVE_IOS_URL_SCHEME,
  toGoogleReversedClientId(GOOGLE_NATIVE_IOS_CLIENT_ID)
);
export const NATIVE_OAUTH_REDIRECT_SCHEME = normalizeOptionalValue(
  import.meta.env.VITE_NATIVE_OAUTH_REDIRECT_SCHEME,
  defaultNativeOAuthRedirectScheme
);
export const NATIVE_BILLING_REDIRECT_SCHEME = normalizeOptionalValue(
  import.meta.env.VITE_NATIVE_BILLING_REDIRECT_SCHEME,
  defaultNativeBillingRedirectScheme
);
export const NATIVE_BILLING_REDIRECT_URL = `${NATIVE_BILLING_REDIRECT_SCHEME.replace(/:$/, '')}://billing/return`;
export const NATIVE_BILLING_RETURN_URL = `${API_BASE_URL}/billing/native-return`;

/** Native In-App Purchase Produkt-IDs */
export const IAP_PREMIUM_PRODUCT_ID_IOS = normalizeOptionalValue(
  import.meta.env.VITE_IAP_PREMIUM_PRODUCT_ID_IOS,
  defaultIapPremiumProductId
);
export const IAP_PREMIUM_PRODUCT_ID_ANDROID = normalizeOptionalValue(
  import.meta.env.VITE_IAP_PREMIUM_PRODUCT_ID_ANDROID,
  defaultIapPremiumProductId
);
export const IAP_PREMIUM_PRODUCT_ID_MACOS = normalizeOptionalValue(
  import.meta.env.VITE_IAP_PREMIUM_PRODUCT_ID_MACOS,
  IAP_PREMIUM_PRODUCT_ID_IOS || defaultIapPremiumProductId
);
export const ANDROID_PACKAGE_NAME = normalizeOptionalValue(
  import.meta.env.VITE_ANDROID_PACKAGE_NAME,
  defaultAndroidPackageName
);
